import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { gsap } from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import { WINDOW } from '@ng-web-apis/common';

gsap.registerPlugin(ScrollToPlugin);

@Directive({
  selector: '[i2lAutoScrollTo]',
})
export class AutoScrollToDirective
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input()
  i2lAutoScrollTo!: boolean;

  @Input()
  windowMode = false;

  @Input()
  direction: 'x' | 'y' = 'x';

  #observer!: ResizeObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    @Inject(WINDOW) private window: Window
  ) {}

  ngOnDestroy(): void {
    if (this.#observer) {
      const { nativeElement } = this.el;
      const parentElement = nativeElement.parentElement;
      if (parentElement) {
        this.#observer.unobserve(parentElement);
      }
    }
  }

  ngAfterViewInit(): void {
    const { nativeElement } = this.el;
    const parentElement = nativeElement.parentElement;
    if (parentElement) {
      this.#observer = new ResizeObserver((entries) => {
        this.updateScrollPosition();
      });
      this.#observer.observe(parentElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['i2lAutoScrollTo']) {
      this.updateScrollPosition();
    }
  }

  updateScrollPosition() {
    if (this.i2lAutoScrollTo) {
      const { nativeElement } = this.el;
      let scrollableElement: HTMLElement | Window;
      let parentSize: number;
      const elementPosition =
        this.direction === 'x'
          ? nativeElement.offsetLeft
          : nativeElement.offsetTop;
      const elementSize =
        this.direction === 'x'
          ? nativeElement.offsetWidth
          : nativeElement.offsetHeight;
      let elementFinalPosition: number | undefined = undefined;
      if (!this.windowMode) {
        if (nativeElement.parentElement) {
          scrollableElement = nativeElement.parentElement as HTMLElement;
          parentSize =
            this.direction === 'x'
              ? scrollableElement.clientWidth
              : scrollableElement.clientHeight;
          elementFinalPosition =
            elementPosition - (parentSize - elementSize) / 2;
        }
      } else {
        scrollableElement = this.window;
        parentSize =
          this.direction === 'x'
            ? scrollableElement.innerWidth
            : scrollableElement.innerHeight;
        elementFinalPosition = elementPosition - (parentSize - elementSize) / 2;
      }
      if (elementFinalPosition) {
        const scrollTo = {};
        scrollTo[this.direction] = elementFinalPosition;
        gsap.to(this.windowMode ? this.window : nativeElement.parentElement, {
          duration: 0.4,
          scrollTo,
        });
      }
    }
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [AutoScrollToDirective],
  exports: [AutoScrollToDirective],
})
export class AutoScrollToDirectiveModule {}
